import React, { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { getDealById } from './crmApi'
import LeadScreen from './LeadScreen'
import { SkeletonPage } from '../shared/Skeleton'

/**
 * /crm/lead/:id — the lead as a PROPER SCREEN (Lee, 17 Sep 2026), not a pop-up:
 * back bar, stage progression strip, then the full existing lead body (same
 * data, statuses and tabs as before — one component, new shell). Optional
 * ?tab=email etc. selects the opening tab.
 */
export const CrmLeadPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const [params] = useSearchParams()
  const navigate = useNavigate()
  const [deal, setDeal] = useState<any | null>(null)
  const [missing, setMissing] = useState(false)
  const load = () => getDealById(String(id)).then(setDeal).catch(() => setMissing(true))
  useEffect(() => { setDeal(null); setMissing(false); load() }, [id]) // eslint-disable-line react-hooks/exhaustive-deps

  if (missing) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-sm text-txt-tertiary">Lead not found. <button className="text-brand-600 hover:underline" onClick={() => navigate('/crm/leads')}>Back to leads</button></p>
      </div>
    )
  }
  if (!deal) return <SkeletonPage className="max-w-4xl" />
  // LEAD SCREEN REDESIGN (Lee, 30 Oct 2026): the new screen replaces DealDetail outright.
  return <LeadScreen deal={deal} initialTab={params.get('tab') || undefined} onChanged={load} />
}

/**
 * Drop-in replacement for the old `{openDeal && <DealDetail …/>}` overlays
 * (Lee, 17 Sep 2026 — "complete screens, not pop-ups"): every screen that used
 * to open the lead in an overlay keeps its exact click logic, but the moment a
 * deal is selected this redirects to the full lead screen instead.
 */
export const LeadRedirect: React.FC<{ deal: { id: string } | null; tab?: string; onDone: () => void }> = ({ deal, tab, onDone }) => {
  const navigate = useNavigate()
  useEffect(() => {
    if (deal?.id) { navigate(`/crm/lead/${deal.id}${tab ? `?tab=${encodeURIComponent(tab)}` : ''}`); onDone() }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deal?.id])
  return null
}
